<template>
  <v-container fluid>
    <v-row justify="center">
      <v-col
        cols="12"
        md="12"
      >
        <base-material-card
          color="info"
          :title="customer.name + vatTitle"
        >
          <v-form>
            <v-container class="py-0">
              <v-row>
                <v-col
                  v-if="!!customer.contactName || !!customer.contactEmail || !!customer.contactPhone"
                  md="6"
                >
                  <h4 class="material">
                    {{ $t('Pages.CustomerDetails.Titles.Contact') }}
                  </h4>
                  <v-divider class="mt-3" />
                  <div v-if="!!customer.contactName">
                    <h6 class="title text-uppercase">
                      {{ $t('Pages.CustomerDetails.Titles.Name') }}
                    </h6>
                    <p class="ml-3 material">
                      {{ customer.contactName }}
                    </p>
                  </div>
                  <div v-if="!!customer.contactEmail">
                    <h6 class="title text-uppercase">
                      {{ $t('Pages.CustomerDetails.Titles.Email') }}
                    </h6>
                    <p class="ml-3 material">
                      {{ customer.contactEmail }}
                    </p>
                  </div>
                  <div v-if="!!customer.contactPhone">
                    <h6 class="title text-uppercase">
                      {{ $t('Pages.CustomerDetails.Titles.Phone') }}
                    </h6>
                    <p class="ml-3 material">
                      {{ customer.contactPhone }}
                    </p>
                  </div>
                </v-col>
                <v-col md="6">
                  <h4 class="material">
                    {{ $t('Pages.CustomerDetails.Titles.Delivery') }}
                  </h4>
                  <v-divider class="mt-3" />
                  <h6 class="title text-uppercase">
                    {{ $t('Pages.CustomerDetails.Titles.Address') }}
                  </h6>
                  <p class="ml-3 material">
                    {{ customer.address }}
                  </p>
                  <h6 class="title text-uppercase">
                    {{ $t('Pages.CustomerDetails.Titles.City') }}
                  </h6>
                  <p class="ml-3 material">
                    {{ customer.postalCode }} {{ customer.city }}
                  </p>
                  <h6 class="title text-uppercase">
                    {{ $t('Pages.CustomerDetails.Titles.Country') }}
                  </h6>
                  <p class="ml-3 material">
                    {{ customer.country }}
                  </p>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
          <template v-slot:actions>
            <v-row
              no-gutters
              align="center"
            >
              <v-col
                cols="auto"
                class="mr-auto"
              >
                <v-icon class="mr-1">
                  mdi-menu-left
                </v-icon>
                <router-link
                  :to="{ name: 'Customers', }"
                  class="ml-3 material"
                >
                  <span>{{ $t('General.Links.All', ['customers']) }}</span>
                </router-link>
              </v-col>
              <v-col cols="auto">
                <v-btn
                  v-if="uid"
                  class="font-weight-light v-btn--tight"
                  color="primary"
                  loading:loading.commit
                  :to="{ name: 'Customer', params: { uid: customer.uid }, }"
                >
                  {{ $t('General.Buttons.EditEntity', ['Customer']) }}
                </v-btn>
              </v-col>
            </v-row>
          </template>
        </base-material-card>
      </v-col>
      <v-col>
        <base-material-card
          color="indigo"
          icon="mdi-vuetify"
          inline
          class="px-5 py-3"
        >
          <template v-slot:after-heading>
            <div class="display-2 font-weight-light">
              {{ $t('subscriptions') }}
            </div>
          </template>

          <v-text-field
            v-model="subscriptionsSearch"
            append-icon="mdi-magnify"
            class="ml-auto"
            :label="$t('General.Labels.Search')"
            hide-details
            single-line
            style="max-width: 250px;"
          />

          <v-divider class="mt-3" />

          <v-data-table
            :headers="subscriptionsHeaders"
            :items="subscriptions"
            :search.sync="subscriptionsSearch"
            :sort-desc="[false, false]"
            :hide-default-header="true"
            multi-sort
            class="row-action"
          >
            <template v-slot:header="{ props: { headers } }">
              <thead>
                <tr>
                  <th
                    v-for="(header, i) in headers"
                    :key="`header-${i}`"
                  >
                    {{ $t(header.text) }}
                  </th>
                </tr>
              </thead>
            </template>
            <template v-slot:item="{ item }">
              <tr>
                <td>
                  <v-btn
                    class="px-2 ml-1"
                    color="info"
                    rounded
                    small
                    @click.stop="gotoDetails(item)"
                  >
                    {{ $t('General.Buttons.Details') }}
                  </v-btn>
                </td>
                <td>{{ item.productName }}</td>
                <td>{{ item.quantity }}</td>
                <td>{{ item.type }}</td>
                <td class="text-right">
                  <debounce-v-btn
                    class="px-2 ml-1"
                    color="error"
                    rounded
                    small
                    @click.stop="showCancel(item)"
                  >
                    {{ $t('General.Buttons.Cancel') }}
                  </debounce-v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
        </base-material-card>
      </v-col>
    </v-row>
    <cancel-subscription-dialog
      :visible="cancelDialog"
      :subscription-to-cancel="cancelSubscription"
      @close="handleCancelClose"
    />
  </v-container>
</template>

<script>
  import { mapGetters } from 'vuex'
  import CancelSubscriptionDialog from '../views/components/custom/dialogs/CancelSubscriptionDialog.vue'

  export default {
    components: {
      CancelSubscriptionDialog,
    },
    props: {
      uid: {
        type: String,
        required: false,
        default: null,
      },
    },
    data: () => ({
      customer: {
        uid: '',
        name: '',
        vatNumber: '',
        address: '',
        zip: '',
        city: '',
        country: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
      },
      subscriptionsHeaders: [
        {
          sortable: false,
          text: '',
          width: '1%',
        },
        {
          sortable: true,
          text: 'Pages.CustomerDetails.SubscriptionsHeaders.Product',
          value: 'productName',
        },
        {
          sortable: true,
          text: 'Pages.CustomerDetails.SubscriptionsHeaders.Quantity',
          value: 'quantity',
        },
        {
          sortable: true,
          text: 'Pages.CustomerDetails.SubscriptionsHeaders.Type',
          value: 'type',
        },
        {
          sortable: false,
          text: '',
          width: '1%',
        },
      ],
      subscriptionsSearch: '',
      cancelLicenses: null,
      cancelSubscription: null,
      cancelDialog: false,
    }),
    computed: {
      ...mapGetters(['getProductNameByVariantId', 'getCustomerName', 'getProductTypeByVariantId']),
      customers () {
        return this.$store.state.customers.data
      },
      subscriptions () {
        return this.$store.state.subscriptions.data.filter(subscription => {
          return subscription.customerId === this.customer.uid
        }).map(item => {
          return {
            ...item,
            customerName: this.getCustomerName(item.customerId),
            productName: this.getProductNameByVariantId(item.productVariantId),
            type: this.getProductTypeByVariantId(item.productVariantId),
          }
        })
      },
      vatTitle () {
        return !!this.customer.vatNumber && this.customer.vatNumber.length > 0
          ? ' (' + this.customer.vatNumber + ')'
          : ''
      },
    },
    created: async function () {
      if (!this.uid) {
        return
      }
      if (this.$store.state.subscriptions.data.length < 1) {
        await this.$store.dispatch('RefreshSubscriptions')
      }

      var existingCustomer = this.customers.find(
        element => element.uid === this.uid
      )
      if (!existingCustomer) {
        // refresh the customers, might be first page load, might be some data missing
        await this.$store.dispatch('RefreshCustomers')
        existingCustomer = this.customers.find(
          element => element.uid === this.uid
        )
        if (!existingCustomer) {
          return
        // todo show error
        }
      }
      this.customer.uid = existingCustomer.uid
      this.customer.name = existingCustomer.name
      this.customer.vatNumber = existingCustomer.vatNumber
      this.customer.address = existingCustomer.address
      this.customer.postalCode = existingCustomer.postalCode
      this.customer.city = existingCustomer.city
      this.customer.country = existingCustomer.country
      this.customer.contactName = existingCustomer.contactName
      this.customer.contactEmail = existingCustomer.contactEmail
      this.customer.contactPhone = existingCustomer.contactPhone
    },
    methods: {
      async refreshLicenses () {
        this.$store.dispatch('RefreshLicenses')
      },
      showCancel (item) {
        this.cancelSubscription = item
        this.cancelDialog = true
      },
      handleCancelClose () {
        this.cancelSubscription = null
        this.cancelDialog = false
      },
      gotoDetails (item) {
        this.$router.push({ name: 'Subscription', params: { uid: item.uid } })
      },
    },
  }
</script>
